import './perks.component.scss';

import type { FunctionComponent } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { Provider } from 'react-redux';
import { Router } from 'wouter-preact';
import { useHashLocation } from 'wouter-preact/use-hash-location';

import { LegacyAuthModalState, LoginType } from '../../../auth/models/auth.model';
import { authenticationFlowService } from '../../../auth/services/authentication-flow.service';
import {
  selectAccountSettings,
  selectAuthenticationLoaded,
  selectAuthenticationLoading,
  selectIsAuthenticated,
} from '../../../auth/store/auth.selectors';
import {
  checkAuthentication,
  initializeCustomer,
  loadAccountSettings,
} from '../../../auth/store/auth.thunk';
import { SpinnerComponent } from '../../../common/components/spinner/spinner.component';
import { Pageable } from '../../../common/page/pageable.model';
import { MembershipBanner } from '../../../membership/components/membership-banner/membership-banner.component';
import {
  selectCreatedContract,
  selectCreatingContract,
  selectMemberships,
} from '../../../membership/store/membership.selectors';
import { loadMemberships } from '../../../membership/store/membership.thunk';
import { modalService } from '../../../modal/modal.service';
import type { AccountSettingsResponse } from '../../../models/common.model';
import { AudioPlayerComponent } from '../../../music/components/audio-player/audio-player.component';
import { selectMusicHasActivated, selectPlayerTracks } from '../../../music/store/music.selectors';
import { store } from '../../../store';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getSearchParams } from '../../../utils/window.utils';
import type { PostFilter } from '../../model/post-filter.model';
import { ContentType } from '../../model/tab.interface';
import { PerksHelper } from '../../perks.helper';
import {
  selectContentTabsLoaded,
  selectContentTabsLoading,
  selectLoginTypes,
} from '../../store/perks.selectors';
import { loadContentTabs, loadPerks, loadPosts, loadTabContent } from '../../store/perks.thunk';
import { initMobileStack } from '../perks-nav/mobile-stack';
import { PerksNav } from '../perks-nav/perks-nav.component';
import { PerksPostFilterModal } from '../perks-posts/perks-post-filter-modal/perks-post-filter-modal.component';
import { apCarouselSlot } from '../perks-posts/perks-post-info/mobile-carousel-slots';
import { PostFilterBarContext } from '../perks-posts/post-filter-bar.context';
import { PerksProfileCarousel } from '../perks-profile-carousel/perks-profile-carousel.component';
import { PerksRouter } from '../perks-router/perks-router.component';
import { PerksConfigContext } from './perks-config.context';

export interface PerksProps {
  membershipId?: string;
  display?: 'always' | 'has_access' | 'no_access';
  loggedIn?: 'true' | 'false';
  internalTitle?: string;
  titleSize?: string;
  description?: string;
  columns?: string;
  columnAlignment?: string;
  showSearch?: boolean;
  showBannerImage?: boolean;
  showProgramName?: boolean;
  showTierImage?: boolean;
  showTierName?: boolean;
  showTierDescription?: boolean;
  showQuickAdd?: boolean;
  showRecentPosts?: boolean;
  showVideoTitle?: boolean;
  showVideoDate?: boolean;
  maxWidth?: boolean;
  // CSS Variables
  cardBackgroundColor?: string;
  cardTextColor?: string;
  cardButtonColor?: string;
  cardButtonTextColor?: string;
  columnBorderColor?: string;
  columnBorderWidth?: string;
  borderRadius?: string;
  horizontalPadding?: string;
  verticalPadding?: string;
  pageWidth?: string;
}

/**
 * wouter's hash location keeps a trailing slash (a menu link like `#/music/`
 * resolves to `/music/`), which matches neither `/music` nor `/music/:id`, so
 * nothing renders. Merchant-authored menu links commonly include a trailing
 * slash, so normalize it out before routing. Root `/` is preserved.
 */
const stripTrailingSlash = (path: string): string =>
  path !== '/' ? path.replace(/\/+$/, '') || '/' : path;

/**
 * Plain anchors like the theme's skip-to-content link (`#MainContent`) share
 * the hash with our routing. wouter turns any hash into a path (`/MainContent`),
 * which matches nothing and rendered the 404. Only `#/`-prefixed hashes are
 * routes; anything else keeps the last route so the browser can scroll to the
 * anchor without the app changing pages.
 */
let lastRoutePath = '/';

const useNormalizedHashLocation = Object.assign(
  (): [string, (to: string) => void] => {
    const [location, navigate] = useHashLocation();
    const hash = window.location.hash;
    if (hash === '' || hash === '#' || hash.startsWith('#/')) {
      lastRoutePath = stripTrailingSlash(location);
    }
    return [lastRoutePath, navigate];
  },
  { hrefs: (to: string): string => '#' + to }
);

export const PerksContent: FunctionComponent<PerksProps> = ({
  membershipId,
  display,
  loggedIn,
  internalTitle,
  titleSize,
  description,
  columns,
  columnAlignment,
  showSearch = true,
  showBannerImage = true,
  showProgramName,
  showTierImage,
  showTierName,
  showTierDescription,
  showQuickAdd,
  showRecentPosts = true,
  showVideoTitle = true,
  showVideoDate = true,
  maxWidth = true,
  cardBackgroundColor,
  cardTextColor,
  cardButtonColor,
  cardButtonTextColor,
  columnBorderColor,
  columnBorderWidth,
  borderRadius,
  horizontalPadding,
  verticalPadding,
  pageWidth,
}: PerksProps) => {
  const dispatch = useAppDispatch();
  const isDesignMode = !!(window as any).Shopify?.designMode;
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const authLoaded = useAppSelector(selectAuthenticationLoaded);
  const authLoading = useAppSelector(selectAuthenticationLoading);
  const otaMode = !!getSearchParams()?.get('access');
  const isUnauthenticated = !isAuthenticated && !otaMode;
  const contentTabsLoading = useAppSelector(selectContentTabsLoading);
  const contentTabsLoaded = useAppSelector(selectContentTabsLoaded);
  const creatingContract = useAppSelector(selectCreatingContract);
  const createdContract = useAppSelector(selectCreatedContract);
  const [loginRedirectStarted, setLoginRedirectStarted] = useState(false);
  const loading: boolean =
    contentTabsLoading || creatingContract || (otaMode && authLoading) || loginRedirectStarted;
  const loaded: boolean = contentTabsLoaded || createdContract || (otaMode && authLoaded);
  const loginTypes = useAppSelector(selectLoginTypes);
  const memberships = useAppSelector(selectMemberships);
  const accountSettingsResp: AccountSettingsResponse = useAppSelector(selectAccountSettings);
  const { accountSettings } = accountSettingsResp || {};
  const playerTracks = useAppSelector(selectPlayerTracks);
  const musicHasActivated = useAppSelector(selectMusicHasActivated);
  const mobileStack = useMemo(() => initMobileStack(), []);
  const [hashLocation, navigate] = useHashLocation();
  const [memLocation, setMemLocation] = useState(() => {
    const hash = window.location.hash;
    return stripTrailingSlash(hash.startsWith('#/') ? hash.slice(1) : '/');
  });

  // Use in-memory routing (no URL writes) when the block is embedded on a page
  // for logged-out users: display="no_access" always, or display="always" when unauthed.
  // This prevents #/join from being appended to the host page URL.
  const useMemoryRouting = isDesignMode || display === 'no_access' || (display === 'always' && !isAuthenticated);

  const routerHook = useMemo(() => {
    if (!useMemoryRouting) return useNormalizedHashLocation;
    return (): [string, (to: string) => void] => [stripTrailingSlash(memLocation), setMemLocation];
  }, [useMemoryRouting, memLocation]);

  const location = stripTrailingSlash(useMemoryRouting ? memLocation : hashLocation);
  const [postFilter, setPostFilter] = useState<PostFilter>({});
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const [perksQuery, setPerksQuery] = useState<string>('');

  // The proxy page renders <single-perks-v2> with no display attribute; theme
  // app blocks always pass one (schema default "always"), so !display means we
  // own the whole page and may navigate away. `/` must stay exempt so
  // PerksHome can resolve prospects to /join, and /join itself is the
  // logged-out signup landing. Detail pages (/posts/:id, /music/:id) stay
  // exempt too: those routes are public — posts can be public previews, and
  // locked content renders a teaser that entices visitors to join. OTA
  // (?access=) visitors are logged out by design. This effect fires on
  // authLoaded, before content tabs finish
  // loading and the router mounts, so the original deep link (e.g. #/posts)
  // is captured as returnToUrl before ProtectedRoute can bounce it to /join.
  const isProxyPage = !display && !isDesignMode;

  useEffect(() => {
    if (!isProxyPage || otaMode || loginRedirectStarted) return;
    if (!authLoaded || isAuthenticated) return;
    if (location === '/' || location === '/join') return;
    if (location.startsWith('/posts/') || location.startsWith('/music/')) return;
    setLoginRedirectStarted(true);
    authenticationFlowService.initiateAuthentication({
      state: LegacyAuthModalState.LOGIN,
      allowedStates: [
        LegacyAuthModalState.LOGIN,
        LegacyAuthModalState.RESET_PASSWORD,
        LegacyAuthModalState.CREATE_ACCOUNT,
      ],
      returnToUrl: window.location.href,
    });
  }, [authLoaded, isAuthenticated, location, loginRedirectStarted]);

  const cssVars = useMemo(() => {
    const vars: Record<string, string> = {
      '--cardBackgroundColor': cardBackgroundColor ?? '',
      '--cardTextColor': cardTextColor ?? '',
      '--cardButtonColor': cardButtonColor ?? '',
      '--cardButtonTextColor': cardButtonTextColor ?? '',
      '--columnBorderColor': columnBorderColor ?? '',
      '--columnBorderWidth': columnBorderWidth ?? '',
      '--borderRadius': borderRadius ?? '',
      '--horizontalPadding': horizontalPadding ?? '',
      '--verticalPadding': verticalPadding ?? '',
      '--pageWidth': pageWidth ?? '',
    };

    if (accountSettings?.cardBackgroundColor) {
      vars['--single-settings-bg-color'] = accountSettings.cardBackgroundColor;
    }
    if (accountSettings?.cardBorderColor) {
      vars['--single-settings-brdr-color'] = accountSettings.cardBorderColor;
    }
    if (accountSettings?.cardTextColor) {
      vars['--single-settings-text-color'] = accountSettings.cardTextColor;
    }
    if (accountSettings?.cardButtonBackgroundColor) {
      vars['--single-settings-button-bg-color'] = accountSettings.cardButtonBackgroundColor;
    }
    if (accountSettings?.cardButtonTextColor) {
      vars['--single-settings-button-text-color'] = accountSettings.cardButtonTextColor;
    }
    if (accountSettings?.secondaryButtonBackgroundColor) {
      vars['--single-settings-secondary-button-bg-color'] =
        accountSettings.secondaryButtonBackgroundColor;
    }
    if (accountSettings?.secondaryButtonTextColor) {
      vars['--single-settings-secondary-button-text-color'] =
        accountSettings.secondaryButtonTextColor;
    }
    if (accountSettings?.borderWidth) {
      vars['--single-settings-border-width'] = `${accountSettings.borderWidth}px`;
    }
    if (accountSettings?.borderRadius) {
      vars['--single-settings-border-radius'] = `${accountSettings.borderRadius}px`;
    }
    if (accountSettings?.pageWidth) {
      vars['--single-settings-page-width'] = `${accountSettings.pageWidth}px`;
    }

    return vars;
  }, [
    cardBackgroundColor,
    cardTextColor,
    cardButtonColor,
    cardButtonTextColor,
    columnBorderColor,
    columnBorderWidth,
    borderRadius,
    horizontalPadding,
    verticalPadding,
    pageWidth,
    accountSettings?.cardBackgroundColor,
    accountSettings?.cardBorderColor,
    accountSettings?.cardTextColor,
    accountSettings?.cardButtonBackgroundColor,
    accountSettings?.cardButtonTextColor,
    accountSettings?.secondaryButtonBackgroundColor,
    accountSettings?.secondaryButtonTextColor,
    accountSettings?.borderWidth,
    accountSettings?.borderRadius,
    accountSettings?.pageWidth,
  ]);

  const COLOR_SETTINGS = [
    {
      appBlockProp: 'cardBackgroundColor',
      accountProp: 'cardBackgroundColor',
      appBlockClass: 'app-block-bg-color',
      accountClass: 'acct-settings-bg-color',
    },
    {
      appBlockProp: 'cardTextColor',
      accountProp: 'cardTextColor',
      appBlockClass: 'app-block-text-color',
      accountClass: 'acct-settings-text-color',
    },
    {
      appBlockProp: 'cardButtonColor',
      accountProp: 'cardButtonBackgroundColor',
      appBlockClass: 'app-block-button-bg-color',
      accountClass: 'acct-settings-button-bg-color',
    },
    {
      appBlockProp: 'cardButtonTextColor',
      accountProp: 'cardButtonTextColor',
      appBlockClass: 'app-block-button-text-color',
      accountClass: 'acct-settings-button-text-color',
    },
    {
      appBlockProp: '',
      accountProp: 'secondaryButtonBackgroundColor',
      appBlockClass: '',
      accountClass: 'acct-settings-secondary-button-bg-color',
    },
    {
      appBlockProp: '',
      accountProp: 'secondaryButtonTextColor',
      appBlockClass: '',
      accountClass: 'acct-settings-secondary-button-text-color',
    },
    {
      appBlockProp: 'columnBorderColor',
      accountProp: 'cardBorderColor',
      appBlockClass: 'app-block-border-color',
      accountClass: 'acct-settings-border-color',
    },
    {
      appBlockProp: 'columnBorderWidth',
      accountProp: 'borderWidth',
      appBlockClass: 'app-block-border-width',
      accountClass: 'acct-settings-border-width',
    },
    {
      appBlockProp: 'borderRadius',
      accountProp: 'borderRadius',
      appBlockClass: 'app-block-border-radius',
      accountClass: 'acct-settings-border-radius',
    },
  ];

  const useCustomClasses = (appBlockProps: any, accountSettings: any, settingsConfig: any) => {
    return useMemo(() => {
      return settingsConfig
        .map(({ appBlockProp, accountProp, appBlockClass, accountClass }: any) => {
          if (appBlockProps[appBlockProp]) return appBlockClass;
          if (accountSettings?.[accountProp]) return accountClass;
          return null;
        })
        .filter(Boolean)
        .join(' ');
    }, [appBlockProps, accountSettings, settingsConfig]);
  };

  const perksClasses = useCustomClasses(
    {
      cardBackgroundColor,
      cardTextColor,
      cardButtonColor,
      cardButtonTextColor,
      columnBorderColor,
      columnBorderWidth,
      borderRadius,
    },
    accountSettings,
    COLOR_SETTINGS
  );

  useEffect(() => {
    dispatch(checkAuthentication({ loginTypes: [LoginType.CUSTOMER] }));
    dispatch(initializeCustomer());
    dispatch(loadAccountSettings());
    dispatch(loadMemberships());
  }, [dispatch]);

  useEffect(() => {
    if (isDesignMode) {
      dispatch(loadContentTabs());
      return;
    }
    if (!authLoaded) return;
    authenticationFlowService.processStoredAuthenticationResult();
    if (otaMode) return;
    dispatch(loadContentTabs());
  }, [authLoaded, isDesignMode]);

  const ROUTE_TO_CONTENT_TYPE: Partial<Record<string, ContentType>> = {
    '/videos': ContentType.VIDEO,
    '/merch': ContentType.PRODUCT,
    '/music': ContentType.ALBUM,
    '/codes': ContentType.PROMOTION,
    '/collectibles': ContentType.COLLECTIBLE,
  };

  useEffect(() => {
    if (!contentTabsLoaded) return;
    const contentType = ROUTE_TO_CONTENT_TYPE[location];
    if (contentType) {
      dispatch(loadTabContent({ contentType }));
    }
  }, [contentTabsLoaded, location]);

  useEffect(() => {
    if (!authLoaded) return;
    if (!isAuthenticated && location.startsWith('/music')) return;
    if (location.startsWith('/posts/')) return;
    dispatch(
      loadPosts({
        pageable: Pageable.of(
          0,
          PerksHelper.POSTS_DEFAULT_PAGE_SIZE,
          PerksHelper.POSTS_DEFAULT_SORT
        ),
        filter: postFilter,
      })
    );
  }, [postFilter, isAuthenticated, location, authLoaded]);

  const shouldDisplay = (): boolean => {
    if (isDesignMode) return true;
    return (
      !display ||
      display === 'always' ||
      (display === 'has_access' && isAuthenticated) ||
      (display === 'no_access' && !isAuthenticated)
    );
  };

  const shouldNotDisplay = (): boolean => {
    return !shouldDisplay();
  };

  const hideCompletely = loaded && !loading && shouldNotDisplay();

  useEffect(() => {
    setPerksQuery('');
  }, [location]);

  // #single-mobile-stack is mounted on document.body, outside this component's
  // wrapper div where the --single-settings-* vars are defined, so elements in
  // the stack (comment bar submit button) can't see the account button colors.
  // The audio player sidesteps this by inlining the vars on its own bar; give
  // the rest of the stack the same button values so they resolve identically.
  useEffect(() => {
    if (accountSettings?.cardButtonBackgroundColor) {
      mobileStack.style.setProperty(
        '--single-settings-button-bg-color',
        accountSettings.cardButtonBackgroundColor
      );
    }
    if (accountSettings?.cardButtonTextColor) {
      mobileStack.style.setProperty(
        '--single-settings-button-text-color',
        accountSettings.cardButtonTextColor
      );
    }
  }, [
    mobileStack,
    accountSettings?.cardButtonBackgroundColor,
    accountSettings?.cardButtonTextColor,
  ]);

  const handleSearch = (query: string) => {
    if (location === '/posts') {
      setPostFilter((prev) => ({ ...prev, query }));
    } else {
      // Store query for videos/merch to filter client-side
      setPerksQuery(query);
    }
  };

  const openPostsFilterModal = (tagId?: string) => {
    const tagIds = postFilter.tagIds ?? [];
    modalService.open({
      component: PerksPostFilterModal,
      // Resolve the radius the way cards do (block setting, account setting,
      // card default) — the wrapper's stylesheet default is 20px, not 8px
      borderRadius:
        borderRadius ||
        (accountSettings?.borderRadius ? `${accountSettings.borderRadius}px` : '8px'),
      props: {
        postFilterProp:
          tagId && !tagIds.includes(tagId)
            ? { ...postFilter, tagIds: [...tagIds, tagId] }
            : postFilter,
        cssVars,
        onClose: (result: PostFilter) => {
          if (result) {
            setPostFilter(result);
            setHasActiveFilters(
              (result.types?.length ?? 0) > 0 ||
                (result.tagIds?.length ?? 0) > 0 ||
                (result.authors?.length ?? 0) > 0
            );
          }
          modalService.closeAll();
        },
      },
    });
  };

  // Access-code visitors get no search bar -- the same guard the bar carried when it lived here.
  const postFilterBar = {
    visible: showSearch && loginTypes?.length === 0,
    hasActiveFilters,
    filter: postFilter,
    onSearch: handleSearch,
    // a single post page has no feed to filter, so its tag badges stay plain (SNGL-7550)
    onOpenFilterModal: location.startsWith('/posts/') ? undefined : openPostsFilterModal,
  };

  const perksConfig = useMemo(
    () => ({
      membershipId,
      internalTitle,
      titleSize,
      description,
      columns,
      columnAlignment,
      showSearch,
      showProgramName,
      showTierImage,
      showTierName,
      showTierDescription,
      showQuickAdd,
      showRecentPosts,
      showVideoTitle,
      showVideoDate,
      searchQuery: perksQuery,
      cardBackgroundColor,
      cardTextColor,
      cardButtonColor,
      cardButtonTextColor,
      columnBorderColor,
      columnBorderWidth,
      borderRadius,
    }),
    [
      membershipId,
      internalTitle,
      titleSize,
      description,
      columns,
      columnAlignment,
      showSearch,
      showProgramName,
      showTierImage,
      showTierName,
      showTierDescription,
      showQuickAdd,
      showRecentPosts,
      showVideoTitle,
      showVideoDate,
      perksQuery,
      cardBackgroundColor,
      cardTextColor,
      cardButtonColor,
      cardButtonTextColor,
      columnBorderColor,
      columnBorderWidth,
      borderRadius,
    ]
  );

  return (
    <PerksConfigContext.Provider value={perksConfig}>
      {!hideCompletely && (
        <Router hook={routerHook}>
          <div class={`single-perks ${perksClasses}`} style={cssVars}>
            {loading && (
              <section className="single-loading">
                <SpinnerComponent />
              </section>
            )}

            {loaded && !loading && (
              <div
                class={`single-perks-wrapper ${isUnauthenticated ? 'single-perks-landing-page' : ''}`}
              >
                {memberships && memberships.length > 0 && (
                  <MembershipBanner
                    accountSettings={accountSettings}
                    showBanner={isUnauthenticated}
                    showBannerImage={showBannerImage}
                    showHeader={isUnauthenticated && location === '/join'}
                    header={internalTitle}
                    showSubheader={isUnauthenticated && location === '/join'}
                    subheader={description}
                    headerSize={titleSize ?? 'h1 single-h1'}
                  />
                )}

                <div
                  class={`perks-container ${maxWidth ? 'page-width' : ''} ${isUnauthenticated ? 'single-not-logged-in' : ''}`}
                >
                  {isAuthenticated && shouldDisplay() && (
                    <PerksNav hasActiveFilters={hasActiveFilters} postFilter={postFilter} />
                  )}
                  {/* My Account replaces the carousel with a permanent wallet
                      pass section + read-only profile (SNGL-7029). */}
                  {isAuthenticated &&
                    shouldDisplay() &&
                    location !== '/my-account' &&
                    !location.startsWith('/music/') && <PerksProfileCarousel />}

                  {/* The posts search + filter bar draws itself inside the feed's left column
                      (PerksPostsComponent); the state behind it stays here, where it feeds
                      loadPosts. */}
                  <PostFilterBarContext.Provider value={postFilterBar}>
                    <PerksRouter />
                  </PostFilterBarContext.Provider>
                </div>
              </div>
            )}
          </div>
        </Router>
      )}
      {musicHasActivated && (
        <AudioPlayerComponent
          tracks={playerTracks}
          portalTarget={apCarouselSlot.value ?? mobileStack}
          onNavigateToAlbum={useMemoryRouting ? undefined : navigate}
        />
      )}
    </PerksConfigContext.Provider>
  );
};

export const PerksComponent: FunctionComponent<PerksProps> = (props) => {
  return (
    <Provider store={store}>
      <PerksContent {...props} />
    </Provider>
  );
};
